/*
 * Spark "Build your box" — produces a REAL Loop bundle (one grouped subscription
 * with the tiered box discount), not N separate subscriptions.
 *
 * On "Add my box to cart":
 *   1. POST bundle.loopwork.co/api/transactions/create
 *        { bundleId, bundleVariantId, bundleDiscountId, sellingPlanId }  -> loopBundleGuid
 *   2. cart/add.js each variant with its Bundle & Save selling_plan + properties._bundleId
 *   3. GET {routes.root}discount/<tierCode>   (the quantity-tiered % off)
 * Loop's order webhook matches _bundleId to the registered transaction and merges
 * the lines into one discounted bundle. Skipping step 1 was the old "separate subs" bug.
 *
 * Box = 2-4 items; tiers 2->15% / 3->20% / 4->30% (they move — Loop's live config wins
 * over the embedded fallback, and the page's tier sentence is written from it). Config comes from the embedded
 * JSON and is refreshed live from Loop so discount ids/codes stay current.
 *
 * THE BOX DISCOUNT IS A SUBSCRIPTION OFFER. The tiered % is only earned when the box is
 * booked on a delivery schedule — a One-time box is priced at full one-time prices, gets
 * no tier code and no Loop transaction (nothing to reconcile without a subscription).
 * Everything that quotes a percentage goes through activeTier(), which returns null on
 * One time, so the badge, the discount row, the total and the CTA can never promise a
 * saving the cart would refuse to give. The offer is not hidden there — it is turned into
 * the nudge: "Subscribe & save N%".
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-spark-bbx]');
  if (!root) return;

  var BOX_NAME = root.getAttribute('data-box-name') || 'Bundle & Save';
  var cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var cfg;
  try { cfg = JSON.parse(root.querySelector('[data-spark-bbx-config]').textContent); }
  catch (e) { return; }
  cfg.tiers = (cfg.tiers || []).slice().sort(function (a, b) { return b.minQty - a.minQty; });

  function money(cents) { return '$' + ((Number(cents) || 0) / 100).toFixed(2); }
  function planByLabel(label) {
    for (var i = 0; i < (cfg.plans || []).length; i++) if (cfg.plans[i].label === label) return cfg.plans[i];
    return null;
  }
  // SPARK-BBX-DISCOUNT-RULE-START
  // The one rule that decides who gets the box discount, kept pure (no DOM, no cfg) so
  // tests/bundle-discount-rule.test.mjs can exercise the shipped source rather than a copy.
  //
  // `tiers` is Loop's tier list; `oneTime` is true when the shopper has chosen One time.

  // The tier a quantity EARNS, on a subscription. For "here is what subscribing would give
  // you" copy — never for pricing the box in front of the shopper.
  function tierForQty(tiers, qty) {
    for (var i = 0; i < tiers.length; i++) if (qty >= tiers[i].minQty) return tiers[i];
    return null;
  }
  // The tier actually APPLIED. Null on a one-time box: the tiers are a subscription offer,
  // so a one-time box pays full one-time prices. Everything that shows or charges a
  // percentage goes through here.
  function appliedTier(tiers, qty, oneTime) { return oneTime ? null : tierForQty(tiers, qty); }
  // ...and the same rule at the cart: only a box that is BOTH big enough and booked on a
  // real selling plan is registered with Loop and given the tier's discount code. Everything
  // else is a plain add. `plan` is the bundle selling plan for the chosen cadence, if any.
  function usesBundleFlow(qty, minItems, oneTime, plan) {
    return qty >= minItems && !oneTime && !!plan;
  }
  // SPARK-BBX-DISCOUNT-RULE-END

  function tierFor(qty) { return tierForQty(cfg.tiers, qty); }
  function activeTier(qty) { return appliedTier(cfg.tiers, qty, isOneTime()); }

  // Refresh config from Loop so bundleVariantId / tier ids / codes / plans stay current.
  (function refreshConfig() {
    if (!cfg.readUrl || !cfg.loopBundleId) return;
    fetch(cfg.readUrl + cfg.loopBundleId, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || typeof d.bundleId === 'undefined') return;
        cfg.bundleId = d.bundleId;
        if (d.variants && d.variants[0]) cfg.bundleVariantId = d.variants[0].bundleVariantId;
        if (Array.isArray(d.discounts) && d.discounts.length) {
          cfg.tiers = d.discounts.map(function (x) {
            return { minQty: x.minConditionValue, discountId: x.bundleDiscountId, code: x.code, pct: x.value };
          }).sort(function (a, b) { return b.minQty - a.minQty; });
        }
        if (Array.isArray(d.sellingPlans) && d.sellingPlans.length) {
          cfg.plans = d.sellingPlans.map(function (x) {
            return { loop: x.sellingPlanId, shopify: String(x.adminGraphQlApiId).split('/').pop(), label: x.name };
          });
        }
        // ELIGIBILITY IS LOOP'S TO DECIDE. The cards are rendered from a handle list in
        // the Liquid (Liquid cannot read this config at build time), so a product Loop has
        // dropped stays on the page until someone notices — which is how a legacy SKU came
        // to be offered as a box item Loop could not reconcile. Hide anything Loop does not
        // list. Fails OPEN: an empty/failed fetch hides nothing.
        if (Array.isArray(d.products) && d.products.length) {
          var eligible = {};
          d.products.forEach(function (p) { if (p && p.shopifyProductId) eligible[String(p.shopifyProductId)] = 1; });
          cards.forEach(function (c) { c.ineligible = !eligible[String(c.data.id)]; });
          applyGender();
        }
        writeTierCopy();
        recompute();
      })
      .catch(function () {});
  })();

  // ---- cards ---------------------------------------------------------------
  var cards = [];
  root.querySelectorAll('[data-spark-bbx-card]').forEach(function (el) {
    var dataEl = el.querySelector('[data-spark-bbx-data]');
    if (!dataEl) return;
    var data; try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var card = {
      el: el, data: data, gender: el.getAttribute('data-gender') || 'all',
      selectedVariantId: String(data.defaultVariant), added: false, qty: 1,
      btnPriceEl: el.querySelector('[data-spark-bbx-btnprice]'),
      addBtn: el.querySelector('[data-spark-bbx-add]'),
      plusBtn: el.querySelector('[data-spark-bbx-plus]'),
      qtyEl: el.querySelector('[data-spark-bbx-qty]')
    };
    cards.push(card);

    // Size and colour both resolve the same variant, so they share one handler.
    el.querySelectorAll('[data-spark-bbx-size], [data-spark-bbx-color]').forEach(function (input) {
      input.addEventListener('change', function () {
        var vid = variantIdFor(card);
        if (vid) card.selectedVariantId = String(vid);
        refreshCard(card); recompute();
      });
    });
    card.addBtn.addEventListener('click', function () {
      if (totalItems() >= cfg.maxItems) return;
      card.added = true; card.qty = card.qty || 1; refreshCard(card); recompute();
    });
    el.querySelector('[data-spark-bbx-minus]').addEventListener('click', function () {
      card.qty -= 1; if (card.qty <= 0) { card.added = false; card.qty = 1; } refreshCard(card); recompute();
    });
    card.plusBtn.addEventListener('click', function () {
      if (totalItems() >= cfg.maxItems) return;
      card.qty += 1; refreshCard(card); recompute();
    });
    refreshCard(card);
  });

  // Resolve the variant from EVERY control the card renders, not just size. Products like
  // Premium Maximum Plus Underwear for Women are Size x Colour: matching on size alone
  // returned whichever colour sat first in the variant list, so the shopper got a colour
  // they never chose — and, because availability was not considered either, potentially one
  // that is out of stock while another colour of the same size is on the shelf.
  //
  // Prefers an AVAILABLE match and falls back to a matching-but-unavailable one, so the
  // card can still show a sold-out state rather than silently selecting nothing.
  function variantIdFor(card) {
    var el = card.el;
    var sizeEl = el.querySelector('[data-spark-bbx-size]');
    var colorEl = el.querySelector('[data-spark-bbx-color]:checked');
    var wantSize = sizeEl ? sizeEl.value : null;
    var wantColor = colorEl ? colorEl.value : null;
    var v = card.data.variants;
    var fallback = null;
    for (var id in v) {
      if (wantSize !== null && v[id].size !== wantSize) continue;
      if (wantColor !== null && v[id].color !== wantColor) continue;
      if (v[id].available) return id;
      if (!fallback) fallback = id;
    }
    return fallback;
  }
  function currentFreq() { var c = root.querySelector('[data-spark-bbx-freq]:checked'); return c ? c.value : ''; }
  function isOneTime() { var c = root.querySelector('[data-spark-bbx-freq]:checked'); return !c || c.hasAttribute('data-onetime') || c.value === ''; }
  function totalItems() { var n = 0; cards.forEach(function (c) { if (c.added) n += c.qty; }); return n; }

  function refreshCard(card) {
    var v = card.data.variants[card.selectedVariantId];
    // Button price follows the selected cadence. On a subscription, show the plan
    // price with the one-time price crossed out.
    if (card.btnPriceEl) {
      var oneTime = v ? v.price : 0;
      var cp = cadencePrice(card);
      if (!isOneTime() && cp < oneTime) {
        card.btnPriceEl.innerHTML = '· ' + money(cp) + ' <s class="opacity-70">' + money(oneTime) + '</s>';
      } else {
        card.btnPriceEl.textContent = '· ' + money(cp);
      }
    }
    card.el.setAttribute('data-added', card.added ? 'true' : 'false');
    card.addBtn.setAttribute('aria-pressed', card.added ? 'true' : 'false');
    if (card.qtyEl) card.qtyEl.textContent = String(card.qty);
    if (v && v.available === false) card.addBtn.disabled = true;
  }
  // Freeze "add more" once the box is full (max items reached).
  function refreshCaps() {
    var full = totalItems() >= cfg.maxItems;
    cards.forEach(function (c) {
      if (c.plusBtn) c.plusBtn.disabled = full;
      if (!c.added && !(c.data.variants[c.selectedVariantId] || {}).available === false) c.addBtn.disabled = full;
    });
  }

  // ---- gender filter -------------------------------------------------------
  function currentGender() { var c = root.querySelector('[data-spark-bbx-gender]:checked'); return c ? c.value : 'all'; }
  function applyGender() {
    var g = currentGender();
    cards.forEach(function (card) {
      var show = !card.ineligible && (g === 'all' || card.gender === 'all' || card.gender === g);
      card.el.style.display = show ? '' : 'none';
    });
    root.querySelectorAll('[data-spark-bbx-cat]').forEach(function (sec) {
      var any = Array.prototype.some.call(sec.querySelectorAll('[data-spark-bbx-card]'), function (c) { return c.style.display !== 'none'; });
      sec.style.display = any ? '' : 'none';
    });
  }

  // ---- "Your box" summary (right rail on desktop / bottom sheet on mobile) --
  var summary = root.querySelector('[data-spark-bbx-summary]');
  var itemsEl = root.querySelector('[data-spark-bbx-items]');
  var saveBadge = root.querySelector('[data-spark-bbx-save-badge]');
  var progressTextEl = root.querySelector('[data-spark-bbx-progress-text]');
  var subtotalEl = root.querySelector('[data-spark-bbx-subtotal]');
  var discountRow = root.querySelector('[data-spark-bbx-discount-row]');
  var discountEl = root.querySelector('[data-spark-bbx-discount]');
  var discountLabel = root.querySelector('[data-spark-bbx-discount-label]');
  var totalEl = root.querySelector('[data-spark-bbx-total]');
  var totalMiniEl = root.querySelector('[data-spark-bbx-total-mini]');
  var saveMiniEl = root.querySelector('[data-spark-bbx-save-mini]');
  var freqLabelEl = root.querySelector('[data-spark-bbx-freqlabel]');
  var countEls = root.querySelectorAll('[data-spark-bbx-count]');
  var cta = root.querySelector('[data-spark-bbx-cta]');
  var toggle = root.querySelector('[data-spark-bbx-toggle]');
  var toggleLabel = root.querySelector('[data-spark-bbx-toggle-label]');
  var switchBtn = root.querySelector('[data-spark-bbx-switch]');
  var switchPctEl = root.querySelector('[data-spark-bbx-switch-pct]');

  function chosen() { return cards.filter(function (c) { return c.added; }); }
  function cardByPid(pid) { for (var i = 0; i < cards.length; i++) if (String(cards[i].data.id) === pid) return cards[i]; return null; }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]; }); }

  // The tier sentence under step 3, written from the tiers rather than hardcoded in the
  // markup — the percentages are merchant data and they move (they were 20/25/30 before
  // Loop was re-cut to 15/20/30, and the sentence went on promising 25% long after the
  // badge and the nudge below had followed the live config).
  var tierCopyEl = root.querySelector('[data-spark-bbx-tier-copy]');
  function writeTierCopy() {
    if (!tierCopyEl || !cfg.tiers.length) return;
    var asc = cfg.tiers.slice().sort(function (a, b) { return a.minQty - b.minQty; });
    var parts = asc.map(function (t, i) {
      return i === 0 ? 'Add ' + t.minQty + ' items to save ' + t.pct + '%' : t.minQty + ' to save ' + t.pct + '%';
    });
    var last = parts.pop();
    // "on every subscription delivery", not "on every delivery": the tiers are earned by
    // subscribing, and this sentence is the page's headline promise.
    tierCopyEl.textContent = parts.join(', ') + (parts.length ? ', or ' : '') + last + ' \u2014 on every subscription delivery.';
  }

  // Next-tier nudge — the key CRO lever. Badge (current saving) + text (what's next).
  function updateProgress(count) {
    var cur = tierFor(count), next = null, badge = '', text = '';
    cfg.tiers.forEach(function (t) { if (t.minQty > count && (!next || t.minQty < next.minQty)) next = t; });
    // ONE TIME: no badge and no percentages here. Nothing is being saved, and adding a
    // fourth item no longer changes the price, so the "add 1 more to save N%" ladder would
    // be a promise this box does not keep. The offer is made once, by the Subscribe button
    // directly below — which quotes the whole saving as ONE number. Two different
    // percentages for the same decision is the kind of arithmetic this store must not ask
    // of a shopper.
    if (isOneTime()) {
      if (count === 0) text = 'Add 2 to 4 items to build your box.';
      else if (count < cfg.minItems) text = 'Add 1 more to build your box.';
      else text = 'Your box is ready.';
    }
    else if (count === 0) { text = 'Add 2 to 4 items to build your box.'; }
    else if (count < cfg.minItems) { var first = tierFor(cfg.minItems); text = 'Add 1 more to save ' + (first ? first.pct : '') + '%.'; }
    else if (next && count < cfg.maxItems) { badge = 'Saving ' + (cur ? cur.pct : 0) + '%'; text = 'Add 1 more to save ' + next.pct + '%.'; }
    else { badge = 'Saving ' + (cur ? cur.pct : 0) + '%'; text = 'Best value — your biggest discount.'; }
    if (saveBadge) { saveBadge.textContent = badge; saveBadge.hidden = !badge; }
    if (progressTextEl) progressTextEl.textContent = text;
  }
  function pickPlan(plans, freqLabel) {
    if (!plans) return null;
    if (plans[freqLabel]) return plans[freqLabel];
    var keys = Object.keys(plans); if (!keys.length) return null;
    var want = labelToDays(freqLabel), best = null, bd = Infinity;
    keys.forEach(function (k) { var d = Math.abs(labelToDays(k) - want); if (d < bd) { bd = d; best = plans[k]; } });
    return best;
  }
  function pickPlanId(plans, freqLabel) { var p = pickPlan(plans, freqLabel); return p ? p.id : null; }
  // Per-item price for the currently selected cadence (one-time price, or the plan price).
  function cadencePrice(card) {
    var v = card.data.variants[card.selectedVariantId]; if (!v) return 0;
    if (isOneTime()) return v.price;
    var p = pickPlan(v.bundle, currentFreq()) || pickPlan(v.sub, currentFreq());
    return p ? p.price : v.price;
  }
  // For a single item (not a bundle): the product's own subscription plan.
  function subPlanFor(card, freqLabel) {
    var v = card.data.variants[card.selectedVariantId]; if (!v) return null;
    return pickPlanId(v.sub, freqLabel) || pickPlanId(v.bundle, freqLabel);
  }

  function renderSummary() {
    var count = totalItems();
    var html = '';
    chosen().forEach(function (c) {
      var v = c.data.variants[c.selectedVariantId];
      var size = v && v.size ? '<p class="m-0 text-caption text-muted-foreground">' + esc(v.size) + '</p>' : '';
      var img = c.data.image
        ? '<img class="h-14 w-14 flex-none rounded-chip bg-muted object-contain mix-blend-multiply" src="' + esc(c.data.image) + '" alt="" loading="lazy">'
        : '<span class="h-14 w-14 flex-none rounded-chip bg-muted"></span>';
      html += '<li class="flex items-center gap-3 text-left" data-pid="' + esc(c.data.id) + '">'
        + img
        + '<div class="min-w-0 flex-1">'
        + '<p class="m-0 text-body-sm font-semibold text-foreground line-clamp-1">' + esc(c.data.title) + '</p>' + size
        + '<span class="mt-1 inline-flex items-center gap-2">'
        + '<button type="button" class="h-7 w-7 rounded-full border border-border-3 text-body-sm font-bold text-primary-text" data-dec aria-label="Remove one">&minus;</button>'
        + '<span class="min-w-[1.25rem] text-center text-body-sm font-semibold">' + c.qty + '</span>'
        + '<button type="button" class="h-7 w-7 rounded-full border border-border-3 text-body-sm font-bold text-primary-text disabled:opacity-40" data-inc aria-label="Add one"' + (count >= cfg.maxItems ? ' disabled' : '') + '>+</button>'
        + '</span></div>'
        + '<span class="flex-none text-body-sm font-semibold text-foreground">' + money(cadencePrice(c) * c.qty) + '</span>'
        + '</li>';
    });
    itemsEl.innerHTML = html;
  }

  function recompute() {
    var count = totalItems();
    var subtotal = 0;
    chosen().forEach(function (c) { subtotal += cadencePrice(c) * c.qty; });
    // The tier the box is PRICED on — null on a one-time box, which pays full price.
    var tier = activeTier(count);
    // A single item can still be bought (one-time or subscription); 2-4 is a bundle.
    var valid = count >= 1 && count <= cfg.maxItems;
    var estTotal = tier ? Math.round(subtotal * (1 - tier.pct / 100)) : subtotal;
    var freqTxt = isOneTime() ? 'one-time' : ('every ' + currentFreq().replace(/deliver every /i, '').toLowerCase());

    for (var i = 0; i < countEls.length; i++) countEls[i].textContent = String(count);
    if (summary) summary.hidden = count === 0;
    updateProgress(count);
    renderSummary();

    if (subtotalEl) subtotalEl.textContent = money(subtotal);
    if (discountRow) discountRow.hidden = !tier;
    if (tier) {
      if (discountLabel) discountLabel.textContent = 'Bundle discount (' + tier.pct + '%)';
      if (discountEl) discountEl.textContent = '−' + money(subtotal - estTotal);
    }
    if (totalEl) totalEl.textContent = money(estTotal);
    if (freqLabelEl) freqLabelEl.textContent = freqTxt;
    if (totalMiniEl) totalMiniEl.textContent = money(estTotal) + ' ' + freqTxt;
    if (saveMiniEl) saveMiniEl.textContent = tier ? '· save ' + tier.pct + '%' : '';

    if (cta) {
      cta.disabled = !valid;
      // Only promise a saving when one is actually being applied — a one-time box quotes
      // its price instead.
      cta.textContent = tier ? ('Add my box · save ' + tier.pct + '%')
        : (count >= 1 ? (count >= cfg.minItems ? 'Add my box to cart · ' : 'Add to cart · ') + money(estTotal)
          : 'Add my box to cart');
    }

    // One-time upsell — now the page's main lever, because the box discount lives on the
    // other side of it. The % is the WHOLE difference: the per-item plan discount AND the
    // tiered box discount the one-time box is not getting.
    if (switchBtn) {
      var showSwitch = false;
      if (isOneTime() && count >= 1 && cfg.plans && cfg.plans.length) {
        var ot = 0, sub = 0, lbl = cfg.plans[0].label;
        chosen().forEach(function (c) {
          var vv = c.data.variants[c.selectedVariantId]; var base = vv ? vv.price : 0;
          ot += base * c.qty;
          var p = pickPlan(vv.bundle, lbl) || pickPlan(vv.sub, lbl);
          sub += (p ? p.price : base) * c.qty;
        });
        var subTier = tierFor(count);
        if (subTier) sub = Math.round(sub * (1 - subTier.pct / 100));
        var subPct = ot > 0 ? Math.round((ot - sub) / ot * 100) : 0;
        if (subPct > 0) { if (switchPctEl) switchPctEl.textContent = subPct + '%'; showSwitch = true; }
      }
      switchBtn.hidden = !showSwitch;
    }
    refreshCaps();
  }

  // Edit items from the summary list.
  if (itemsEl) itemsEl.addEventListener('click', function (e) {
    var t = e.target;
    var li = t.closest && t.closest('[data-pid]'); if (!li) return;
    var card = cardByPid(li.getAttribute('data-pid')); if (!card) return;
    if (t.closest('[data-dec]')) { card.qty -= 1; if (card.qty <= 0) { card.added = false; card.qty = 1; } }
    else if (t.closest('[data-inc]')) { if (totalItems() < cfg.maxItems) card.qty += 1; }
    else return;
    refreshCard(card); recompute();
  });

  // One-time upsell → switch to the first subscription cadence.
  if (switchBtn) switchBtn.addEventListener('click', function () {
    var m = root.querySelector('[data-spark-bbx-freq]:not([data-onetime])');
    if (m) { m.checked = true; syncFreqSelect(); cards.forEach(refreshCard); recompute(); }
  });

  // Mobile: expand/collapse the box sheet.
  if (toggle) toggle.addEventListener('click', function () {
    var open = summary.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (toggleLabel) toggleLabel.innerHTML = open ? 'Hide &#9660;' : 'View box &#9650;';
  });

  // The schedule is a <select> (the PDP's control, assets/spark-select.js) over the same
  // radios that have always held the state — spark-bundle-builder.js reads
  // [data-spark-bbx-freq]:checked everywhere, so bridging the two directions leaves every
  // cadence, plan-lookup and cart path untouched.
  //
  // select -> radio dispatches a real `change` on the radio rather than calling the work
  // directly, so there is ONE code path per cadence change (the listener below), whether it
  // was the dropdown, the upsell button, or a no-JS native select that moved it.
  var freqSelect = root.querySelector('[data-spark-bbx-freq-select]');
  if (freqSelect) {
    freqSelect.addEventListener('change', function () {
      var radios = root.querySelectorAll('[data-spark-bbx-freq]');
      for (var i = 0; i < radios.length; i++) {
        if (radios[i].value === freqSelect.value) {
          if (!radios[i].checked) {
            radios[i].checked = true;
            radios[i].dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
      }
    });
  }
  // radio -> select, for the paths that move the radio in code (the one-time upsell).
  // Setting .value fires no event, so the themed face is refreshed by hand.
  function syncFreqSelect() {
    if (!freqSelect) return;
    var c = root.querySelector('[data-spark-bbx-freq]:checked');
    if (!c || freqSelect.value === c.value) return;
    freqSelect.value = c.value;
    if (freqSelect._sparkSelRefresh) freqSelect._sparkSelRefresh();
  }

  root.querySelectorAll('[data-spark-bbx-freq]').forEach(function (i) { i.addEventListener('change', function () { cards.forEach(refreshCard); recompute(); }); });
  root.querySelectorAll('[data-spark-bbx-gender]').forEach(function (i) { i.addEventListener('change', applyGender); });

  // ---- add to cart (the real Loop bundle flow) -----------------------------
  root.addEventListener('submit', function (evt) {
    evt.preventDefault();
    var list = chosen();
    var count = totalItems();
    if (count < 1 || count > cfg.maxItems || cta.disabled) return;

    cta.disabled = true; cta.textContent = 'Adding…';

    var oneTime = isOneTime();
    // The box's own subscription plan for the chosen cadence. Null on One time — and null
    // if Loop's plan list ever drifts out from under a cadence we still offer.
    var plan = oneTime ? null : planByLabel(currentFreq());

    // PLAIN ADD — no Loop transaction, no tier code. Three cases reach it:
    //   • a single item: not a bundle at all;
    //   • a ONE-TIME box: still perfectly buyable, just at one-time prices. The tiered
    //     discount is a subscription offer, so there is no bundle for Loop to register and
    //     no code to apply. Registering one anyway would leave a transaction Loop can never
    //     reconcile (no selling plan), and applying the code would put a discount on the
    //     cart the shopper was never quoted;
    //   • no plan for the chosen cadence: we cannot book the subscription the discount is
    //     conditioned on, so the code must not go on the cart either. Each line falls back
    //     to the product's own subscription plan (subPlanFor) rather than nothing.
    if (!usesBundleFlow(count, cfg.minItems, oneTime, plan)) {
      var singleItems = list.map(function (c) {
        var it = { id: Number(c.selectedVariantId), quantity: c.qty, properties: {} };
        if (!oneTime) { var sp = subPlanFor(c, currentFreq()); if (sp) it.selling_plan = Number(sp); }
        return it;
      });
      addToCart(singleItems)
        .then(function () { window.location.href = cartRoot + 'cart'; })
        .catch(function (err) { console.error('spark-bundle-builder: add failed', err); cta.disabled = false; recompute(); alert('Sorry — something went wrong. Please try again.'); });
      return;
    }

    // Bundle (2-4 items) ON A SCHEDULE — the real Loop bundle flow, and the only path that
    // registers a transaction and applies a tier code.
    cta.textContent = 'Building your box…';
    var tier = tierFor(count);
    var loopPlanId = plan.loop;
    var shopifyPlanId = plan.shopify;

    createTransaction(tier.discountId, loopPlanId)
      .then(function (guid) {
        if (!guid) throw new Error('no loopBundleGuid');
        var items = list.map(function (c) {
          var it = { id: Number(c.selectedVariantId), quantity: c.qty, properties: { _bundleId: guid, _bundleName: BOX_NAME } };
          if (shopifyPlanId) it.selling_plan = Number(shopifyPlanId);
          return it;
        });
        return addToCart(items);
      })
      .then(function () { return applyDiscount(tier.code); })
      .then(function () { window.location.href = cartRoot + 'cart'; })
      .catch(function (err) {
        console.error('spark-bundle-builder: bundle add failed', err);
        cta.disabled = false; recompute();
        alert('Sorry — something went wrong building your box. Please try again.');
      });
  });

  function createTransaction(bundleDiscountId, sellingPlanId) {
    return fetch(cfg.createUrl, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bundleId: cfg.bundleId, bundleVariantId: cfg.bundleVariantId, bundleDiscountId: bundleDiscountId, sellingPlanId: sellingPlanId })
    })
      .then(function (r) { return r.json(); })
      .then(function (d) { return d && d.data && d.data.loopBundleGuid; });
  }
  function addToCart(items) {
    return fetch(cartRoot + 'cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) { if (!res.ok) return res.json().then(function (e) { throw e; }); return res.json(); });
  }
  // Apply the tier's Shopify discount code to the cart (Loop's tiered box discount).
  function applyDiscount(code) {
    if (!code) return Promise.resolve();
    return fetch(cartRoot + 'discount/' + encodeURIComponent(code), { method: 'GET' }).catch(function () {});
  }

  applyGender();
  recompute();
})();
